
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser } from '../hooks/useUser';

import { Box, Paper, Stack, Typography, Avatar, List, ListItem, ListItemAvatar, ListItemText, Button, Skeleton, alpha, Pagination } from '@mui/material';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import theme from '../../../core/components/layout/theme';
import moment from 'moment';
import CustomTypography from '../../../core/components/ui/CustomTypography';
import CustomChipComponent from '../../../core/components/common/custom-chip.component';
import UserProfileListComponent from '../components/user-profile.list.component';
import { useProfile } from '../hooks/useProfile';
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CustomButton from '../../../core/components/ui/CustomButton';


function stringToColor(string) {
  let hash = 0;
  let i;

  /* eslint-disable no-bitwise */
  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  /* eslint-enable no-bitwise */

  return color;
}

function stringAvatar(name) {
  return {
    sx: {
      bgcolor: stringToColor(name), width: 80, height: 80
    },
    children: `${name.split(' ')[0][0]}${name.split(' ')[1][0]}`,
  };
}

export default function UserViewPage() {
   
    const user_uid = new URLSearchParams(window.location.search).get("user_uid");
    
    const navigate = useNavigate();

    const {loadingUser, viewUser, userDetails} = useUser();
    const {loading, getUserProfile, profileList} = useProfile();

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);

    const rowsPerPage = 10;

    const handleChangePagination = (event, page) => { 
        setCurrentPage(page); 
    }

    useEffect(() => {
        if (profileList) {
            setTotalPages(profileList?.totalPages);
        }
    }, [profileList])

    useEffect(() => {
        if (user_uid) {
            let paramters = {
                user_uid: user_uid
            };
            viewUser(paramters);
        }
    }, [user_uid]);

    useEffect(() => {
        if (currentPage) {
           const parameters = {
                page: currentPage ,
                limit: rowsPerPage,
                userUID: user_uid
            };

            getUserProfile(parameters);
        }
    }, [currentPage, user_uid])

    useEffect(() => {
        if (user_uid) {
            let paramters = {
                page: 1,
                limit: rowsPerPage,
                userUID: user_uid
            };
            getUserProfile(paramters);
        }
    }, [user_uid]);

    return (
        <>
         <CustomButton
            variant="text" 
            startIcon={<ArrowBackIcon fontSize="small" />}
            label="Back"
            customStyles={{fontSize: 14, mt: 2}}
            onClick={() => navigate(-1)}
        />
        <Paper elevation={0} sx={{border: `1px solid #e5edf6`, borderRadius: 2, mt: 3}}>
                <Stack
                direction="row"
                spacing={2}
                sx={{
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
                >
                    <Stack
                    direction="row"
                    spacing={2}
                    flex={1}
                    sx={{
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                    >
                        {
                            loadingUser ?
                                <Skeleton variant="text" height="35px" width="70%" sx={{backgroundColor: `${alpha(theme.palette.primary.main, 0.09)}`}} />
                            :
                            <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                                <ListItem>
                                    <ListItemAvatar>
                                        <Avatar  {...stringAvatar(userDetails?.user?.firstname + " " + userDetails?.user?.lastname)} />
                                    </ListItemAvatar>
                                    <ListItemText sx={{pl: 2, width: '100%'}} primary={<Typography variant="h5">{`${userDetails?.user?.lastname}, ${userDetails?.user?.firstname} ${userDetails?.user?.middlename == null ? "" : userDetails?.user?.middlename}`}</Typography>} secondary={<Typography variant="subtitle2">{userDetails?.user?.email}</Typography>} />
                                </ListItem>
                            </List>
                        }
                    </Stack>

                    <Stack
                    direction="column"
                    spacing={2}
                    flex={1}
                    sx={{
                        justifyContent: "center",
                        alignItems: "end",
                        pr: 4
                    }}
                    >
                        <CustomChipComponent category="profileStatusStyles"
                            label={(userDetails?.user?.status === "Active") ? "Active" 
                                : 
                                    (userDetails?.user?.status === "Deactivated") ? "Deactivated"
                                :
                                    (userDetails?.user?.status === "Pending") ? "Pending"
                                :
                                    userDetails?.user?.status
                            }
                        />
                    </Stack>
                </Stack>
            </Paper>

            <Paper elevation={0} sx={{border: `1px solid #e5edf6`, borderRadius: 2, p: 3, mt: 1}}>
                <Stack
                direction="row"
                spacing={2}
                sx={{
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
                >
                    <Box flex={1}>
                        <CustomTypography 
                            variant="h3"
                            text={"User Information"}
                            customStyles={{
                                marginBottom: 0, 
                                fontSize: 20,
                                fontWeight: 700
                            }}
                        />
                    </Box>
                    <Stack
                    direction="column"
                    flex={1}
                    sx={{
                        justifyContent: "center",
                        alignItems: "end",
                    }}
                    >
                        <Button 
                            variant="outlined" 
                            startIcon={<BorderColorIcon />}
                            sx={{textTransform: 'capitalize'}}
                            onClick={() => navigate('/user/update?user_uid=' + userDetails?.user?.user_uid)}
                        >
                            Edit User
                        </Button>
                    </Stack>
                </Stack>

                <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2
                    }}
                    >
                    <Box flex={1}>
                        <CustomTypography 
                            variant="caption" 
                            text={"Lastname"} 
                            customStyles={{
                                marginBottom: 0, 
                            }}
                        />

                        {(loadingUser) ?
                            <Skeleton variant="text" height="35px" width="70%" sx={{backgroundColor: `${alpha(theme.palette.primary.main, 0.09)}`}} />
                        :
                            <CustomTypography
                                variant="body1"
                                text={userDetails?.user?.lastname}
                                customStyles={{
                                    marginBottom: 0, 
                                    fontWeight: 500,
                                }}
                            />
                        }
                    </Box>

                    <Box flex={1}>
                        <CustomTypography 
                            variant="caption" 
                            text={"Firstname"} 
                            customStyles={{
                                marginBottom: 0, 
                            }}
                        />

                        {(loadingUser) ?
                            <Skeleton variant="text" height="35px" width="70%" sx={{backgroundColor: `${alpha(theme.palette.primary.main, 0.09)}`}} />
                        :
                            <CustomTypography
                                variant="body1"
                                text={userDetails?.user?.firstname}
                                customStyles={{
                                    marginBottom: 0, 
                                    fontWeight: 500,
                                }}
                            />
                        }
                    </Box>

                    <Box sx={{display: "inline-grid"}} flex={1}>
                        <CustomTypography 
                            variant="caption" 
                            text={"Middlename"} 
                            customStyles={{
                                marginBottom: 0, 
                            }}
                        />

                        {(loadingUser) ?
                            <Skeleton variant="text" height="35px" width="70%" sx={{backgroundColor: `${alpha(theme.palette.primary.main, 0.09)}`}} />
                        :
                            <CustomTypography
                                variant="body1"
                                text={userDetails?.user?.middlename == null ? "N/A" : userDetails?.user?.middlename}
                                customStyles={{
                                    marginBottom: 0, 
                                    fontWeight: 500,
                                }}
                            />
                        }
                    </Box>
                    <Box flex={1} />
                </Stack>

                <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2
                    }}
                    >
                    <Box flex={1}>
                        <CustomTypography 
                            variant="caption" 
                            text={"Contact Number"} 
                            customStyles={{
                                marginBottom: 0, 
                            }}
                        />

                        {(loadingUser) ?
                            <Skeleton variant="text" height="35px" width="70%" sx={{backgroundColor: `${alpha(theme.palette.primary.main, 0.09)}`}} />
                        :
                            <CustomTypography
                                variant="body1"
                                text={userDetails?.user?.contact_no == null ? "N/A" : userDetails?.user?.contact_no}
                                customStyles={{
                                    marginBottom: 0, 
                                    fontWeight: 500,
                                }}
                            />
                        }
                    </Box>

                    <Box flex={1}>
                        <CustomTypography 
                            variant="caption" 
                            text={"Email"} 
                            customStyles={{
                                marginBottom: 0, 
                            }}
                        />

                        {(loadingUser) ?
                            <Skeleton variant="text" height="35px" width="70%" sx={{backgroundColor: `${alpha(theme.palette.primary.main, 0.09)}`}} />
                        :
                            <CustomTypography
                                variant="body1"
                                text={userDetails?.user?.email}
                                customStyles={{
                                    marginBottom: 0, 
                                    fontWeight: 500,
                                }}
                            />
                        }
                    </Box>

                    <Box sx={{display: "inline-grid"}} flex={1}>
                        <CustomTypography 
                            variant="caption" 
                            text={"Birthdate"} 
                            customStyles={{
                                marginBottom: 0, 
                            }}
                        />

                        {(loadingUser) ?
                            <Skeleton variant="text" height="35px" width="70%" sx={{backgroundColor: `${alpha(theme.palette.primary.main, 0.09)}`}} />
                        :
                            <CustomTypography
                                variant="body1"
                                text={moment(userDetails?.user?.birthdate).format("MMMM DD, YYYY")}
                                customStyles={{
                                    marginBottom: 0, 
                                    fontWeight: 500,
                                }}
                            />
                        }
                    </Box>
                    <Box flex={1} />
                </Stack>
            </Paper>
            <Paper elevation={0} sx={{border: `1px solid #e5edf6`, borderRadius: 2, p: 3, mt: 1}}>
                <CustomTypography 
                    variant="h3"
                    text={"Profiles"}
                    customStyles={{
                        marginBottom: 0, 
                        fontSize: 20,
                        fontWeight: 700
                    }}
                />
                <UserProfileListComponent profileList={profileList} loading={loading} user_uid={user_uid}/>

                <Pagination
                    count={ totalPages}
                    page={currentPage}
                    onChange={handleChangePagination}
                    sx={{
                        margin: "auto",
                        width: "fit-content",
                        paddingTop: 3,
                        "& .MuiPaginationItem-root.Mui-selected": {
                            backgroundColor: "#0b99da",
                            color: "#fff",
                        }
                    }}
                />
            </Paper>
        </>
    );
}